import { Sheet, SheetContent } from '@/components/atoms/sheet';
import { SkillFormContent } from '@/routes/_dashboard/dashboard/settings/skills';
import {
  useCreateSkill,
  type SkillFormValues,
} from '@/components/organisms/skills/skill-form-shared';
import type { ServiceType } from '@/types/scheduler';

interface SkillCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Open with the certification toggle pre-enabled. */
  defaultIsCertification?: boolean;
  /** Fired with the newly created skill so the caller can attach it. */
  onCreated: (skill: ServiceType) => void;
}

/**
 * Launches the standard skill-create form (the exact one from Settings → Skills)
 * as a side sheet, so a technician form can register a new skill/cert inline
 * without leaving the page. Reuses SkillFormContent — never duplicates it.
 */
export function SkillCreateDialog({
  open,
  onOpenChange,
  defaultIsCertification = false,
  onCreated,
}: SkillCreateDialogProps) {
  const createSkill = useCreateSkill();

  function handleCreate(values: SkillFormValues) {
    createSkill.mutate(
      {
        ...values,
        name: values.name.trim(),
        slug: values.slug.trim(),
        defaultValidityMonths: values.isCertification ? values.defaultValidityMonths : null,
      },
      {
        onSuccess: (created) => {
          onCreated(created);
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl"
        showCloseButton={false}
      >
        {open ? (
          <SkillFormContent
            mode="create"
            skill={null}
            isLoading={false}
            isPending={createSkill.isPending}
            onClose={() => onOpenChange(false)}
            onCreate={handleCreate}
            onUpdate={() => undefined}
            initialValues={{ isCertification: defaultIsCertification }}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
